import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { BusinessProvider } from "@/context/BusinessContext";
import { TrialModalProvider } from "@/context/TrialModalContext";
import { DoxPreviewProvider } from "@/context/DoxPreviewContext";
import { DoxWorkProvider } from "@/context/DoxWorkContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import DoxWidget from "@/components/DoxWidget";
import { useLocation } from "react-router-dom";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import KBDrafts from "@/pages/KBDrafts";
import PracticePage from "@/pages/PracticePage";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";
import Verify from "@/pages/Verify";

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);
  return null;
}

/**
 * Hide the persistent DoxWidget on outcome-first "proof" routes so those
 * screens stay pristine (no navigation, no clutter, just proof).
 */
function ConditionalDoxWidget() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/verify/")) return null;
  return <DoxWidget />;
}

function App() {
  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  return (
    <div className="App">
      <AuthProvider>
        <BusinessProvider>
          <BrowserRouter>
            <SmoothScroll />
            <TrialModalProvider>
            <DoxPreviewProvider>
            <DoxWorkProvider>
            <ErrorBoundary label="app">
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/for/:state/:trade" element={<PracticePage />} />
              <Route path="/verify/:recordId" element={<Verify />} />
              <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
              <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
              <Route path="/admin/kb-drafts" element={<ProtectedRoute adminOnly><KBDrafts /></ProtectedRoute>} />
            </Routes>
            </ErrorBoundary>
            <ConditionalDoxWidget />
            </DoxWorkProvider>
            </DoxPreviewProvider>
            </TrialModalProvider>
            <Toaster position="top-right" theme="dark" richColors />
          </BrowserRouter>
        </BusinessProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
